import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const STATUS_COLORS = {
  red: '#c62828',
  yellow: '#f9a825',
};

const FIELDS = [
  {
    section: 'Status Thresholds',
    fields: [
      { key: 'redMissingDays', label: 'Red: Missing days', type: 'int', color: STATUS_COLORS.red, helperText: 'Number of missing days to trigger critical status' },
      { key: 'redStorePct', label: 'Red: Store presence %', type: 'int', color: STATUS_COLORS.red, helperText: 'Below this % of stores present triggers critical' },
      { key: 'redAnomalyCount', label: 'Red: Anomaly count', type: 'int', color: STATUS_COLORS.red, helperText: 'Number of anomalies to trigger critical status' },
      { key: 'yellowMissingDays', label: 'Yellow: Missing days', type: 'int', color: STATUS_COLORS.yellow, helperText: 'Number of missing days to trigger warning status' },
      { key: 'yellowStorePct', label: 'Yellow: Store presence %', type: 'int', color: STATUS_COLORS.yellow, helperText: 'Below this % of stores present triggers warning' },
    ],
  },
  {
    section: 'Store Anomaly Detection',
    fields: [
      { key: 'storeAnomalyMinMedian', label: 'Min median row count', type: 'int', helperText: 'Store must have at least this median to flag anomalies' },
      { key: 'storeAnomalyRatio', label: 'Ratio (as %)', type: 'pct', helperText: 'Row count below this % of median flags an anomaly' },
    ],
  },
  {
    section: 'Day-of-Week Detection',
    fields: [
      { key: 'dowAnomalyMinAvg', label: 'Min avg row count', type: 'int', helperText: 'Day-of-week must have at least this average to flag anomalies' },
      { key: 'dowAnomalyRatio', label: 'Ratio (as %)', type: 'pct', helperText: 'Daily total below this % of day-of-week average flags an anomaly' },
    ],
  },
  {
    section: 'Historical Lookback',
    fields: [
      { key: 'histWeeksBack', label: 'Weeks back', type: 'int', helperText: 'Number of weeks of history used for day-of-week averages' },
    ],
  },
];

const SourceThresholdDialog = ({
  open,
  onClose,
  sourceId,
  sourceName,
  sourceKey,
  categoryKey,
  categoryLabel,
  firebaseUser,
}) => {
  const [values, setValues] = useState({});
  const [categoryDefaults, setCategoryDefaults] = useState({});
  const [overrides, setOverrides] = useState({}); // Which fields have custom values
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    if (!open || !sourceId || !categoryKey) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await firebaseUser.getIdToken(true);

        // Load category defaults and source overrides in parallel
        const [categoryRes, sourceRes] = await Promise.all([
          axios.get(`/hd-api/data-integrity-2/thresholds/${categoryKey}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`/hd-api/data-integrity-2/source-thresholds/${sourceId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (!cancelled) {
          setCategoryDefaults(categoryRes.data.thresholds);

          const sourceThresholds = sourceRes.data.thresholds;
          setUpdatedBy(sourceRes.data.updatedBy);
          setUpdatedAt(sourceRes.data.updatedAt);

          // Determine which fields are overridden (not null)
          const newOverrides = {};
          const mergedValues = {};
          Object.keys(categoryRes.data.thresholds).forEach((key) => {
            if (sourceThresholds[key] !== null && sourceThresholds[key] !== undefined) {
              newOverrides[key] = true;
              mergedValues[key] = sourceThresholds[key];
            } else {
              newOverrides[key] = false;
              mergedValues[key] = categoryRes.data.thresholds[key];
            }
          });
          setOverrides(newOverrides);
          setValues(mergedValues);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading source thresholds:', err);
          setError('Failed to load thresholds.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [open, sourceId, categoryKey, firebaseUser]);

  const handleToggleOverride = (key) => {
    setOverrides((prev) => {
      const newOverrides = { ...prev, [key]: !prev[key] };
      // If disabling override, reset to category default
      if (!newOverrides[key]) {
        setValues((prevVals) => ({ ...prevVals, [key]: categoryDefaults[key] }));
      }
      return newOverrides;
    });
  };

  const handleChange = (key, type) => (e) => {
    const raw = e.target.value;
    if (raw === '') {
      setValues((prev) => ({ ...prev, [key]: '' }));
      return;
    }
    if (type === 'pct') {
      const pct = parseFloat(raw);
      if (!isNaN(pct)) setValues((prev) => ({ ...prev, [key]: pct / 100 }));
    } else {
      const num = parseInt(raw, 10);
      if (!isNaN(num)) setValues((prev) => ({ ...prev, [key]: num }));
    }
  };

  const getDisplayValue = (key, type) => {
    const v = values[key];
    if (v === '' || v == null) return '';
    if (type === 'pct') return Math.round(v * 100);
    return v;
  };

  const getCategoryDisplayValue = (key, type) => {
    const v = categoryDefaults[key];
    if (v === '' || v == null) return '—';
    if (type === 'pct') return `${Math.round(v * 100)}%`;
    return v;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const token = await firebaseUser.getIdToken(true);

      // Build thresholds object with null for inherited values
      const thresholds = {};
      Object.keys(values).forEach((key) => {
        thresholds[key] = overrides[key] ? values[key] : null;
      });

      await axios.put(`/hd-api/data-integrity-2/source-thresholds/${sourceId}`, {
        categoryKey,
        sourceKey,
        thresholds,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSaving(false);
      onClose(true); // Pass true to indicate changes were saved
    } catch (err) {
      console.error('Error saving source thresholds:', err);
      setError('Failed to save thresholds.');
      setSaving(false);
    }
  };

  const handleResetAll = async () => {
    try {
      setSaving(true);
      setError(null);
      const token = await firebaseUser.getIdToken(true);

      await axios.delete(`/hd-api/data-integrity-2/source-thresholds/${sourceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Reset local state
      const resetOverrides = {};
      Object.keys(overrides).forEach((key) => { resetOverrides[key] = false; });
      setOverrides(resetOverrides);
      setValues({ ...categoryDefaults });
      setUpdatedBy(null);
      setUpdatedAt(null);

      setSaving(false);
    } catch (err) {
      console.error('Error resetting source thresholds:', err);
      setError('Failed to reset thresholds.');
      setSaving(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose(false);
  };

  const hasAnyOverrides = Object.values(overrides).some(Boolean);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontFamily: 'Lato', fontWeight: 600 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {sourceName} — Threshold Settings
          <Tooltip title="Override specific thresholds for this data source. Unchecked fields inherit from category settings." arrow>
            <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 400 }}>
          Category: {categoryLabel}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {FIELDS.map(({ section, fields }) => (
              <Box key={section}>
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1 }}>
                  {section}
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {fields.map(({ key, label, type, helperText, color }) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!overrides[key]}
                            onChange={() => handleToggleOverride(key)}
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ fontWeight: overrides[key] ? 600 : 400 }}>
                            Override
                          </Typography>
                        }
                        sx={{ minWidth: 100, m: 0 }}
                      />
                      <TextField
                        label={label}
                        type="number"
                        size="small"
                        value={getDisplayValue(key, type)}
                        onChange={handleChange(key, type)}
                        disabled={!overrides[key]}
                        helperText={overrides[key] ? helperText : `Inherits: ${getCategoryDisplayValue(key, type)}`}
                        sx={{
                          flex: 1,
                          ...(color && overrides[key] && {
                            '& .MuiOutlinedInput-root': {
                              borderLeft: `3px solid ${color}`,
                            },
                          }),
                          ...(!overrides[key] && {
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'action.hover',
                            },
                          }),
                        }}
                        InputProps={type === 'pct' ? {
                          endAdornment: <Typography variant="body2" sx={{ color: 'text.secondary', ml: 0.5 }}>%</Typography>,
                        } : undefined}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}

            {updatedBy && (
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1 }}>
                Last updated by {updatedBy}
                {updatedAt && ` on ${new Date(updatedAt).toLocaleString()}`}
              </Typography>
            )}

            {error && (
              <Typography variant="body2" color="error">{error}</Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleResetAll}
          color="inherit"
          disabled={loading || saving || !hasAnyOverrides}
        >
          Reset to Category Defaults
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SourceThresholdDialog;
